import ms from 'ms';
import delay from 'delay';
import { get, isEqual } from 'lodash';
import ExodusModule from '@exodus/module';
import { synchronizeTime, isFreezable, unwrapErrorMessage } from './helpers';
import generateRemoteConfigUrl from './generate-remote-config-url';
const MODULE_ID = 'remoteConfig';
export class RemoteConfig extends ExodusModule {
    #started = false;
    #current;
    #previous;
    #lastModified;
    #loadedStatusAtom;
    #remoteConfigUrl;
    #getBuildMetadata;
    #logger;
    #fetch;
    #freeze;
    #resolveLoaded;
    #whenLoaded = new Promise((resolve) => {
        this.#resolveLoaded = resolve;
    });
    #config;
    #timer;
    constructor({ logger, freeze, fetch, config, getBuildMetadata, remoteConfigStatusAtom, }) {
        super({ name: MODULE_ID, logger });
        this.#logger = logger;
        this.#fetch = (...args) => fetch(...args);
        this.#freeze = freeze;
        this.#config = config || Object.create(null);
        this.#remoteConfigUrl = config?.remoteConfigUrl;
        this.#getBuildMetadata = getBuildMetadata;
        this.#loadedStatusAtom = remoteConfigStatusAtom;
        this.setMaxListeners(Number.POSITIVE_INFINITY);
    }
    get = async (key) => {
        await this.#whenLoaded;
        return get(this.#current, key);
    };
    getAll = async () => {
        await this.#whenLoaded;
        return this.#current;
    };
    load = async () => {
        if (this.#started)
            return;
        const { fetchInterval = ms('2m'), errorBackoffTime = ms('5s') } = this.#config;
        this.#started = true;
        while (this.#started) {
            try {
                await this.#load();
                this.#timer = delay(fetchInterval);
            }
            catch (err) {
                const errMessage = unwrapErrorMessage(err);
                this.#logger.error(errMessage);
                this.#loadedStatusAtom.set((value) => ({
                    ...value,
                    error: errMessage,
                }));
                this.#timer = delay(errorBackoffTime);
            }
            await this.#timer;
        }
    };
    sync = () => {
        this.#emit('sync', { current: this.#current, previous: this.#previous });
    };
    stop = () => {
        this.#started = false;
        this.#timer?.clear();
    };
    #emit = (name, ...args) => {
        return this.emit(name, ...args.map((arg) => (isFreezable(arg) ? this.#freeze(arg) : arg)));
    };
    #load = async () => {
        const { config, modified } = await this.#fetchConfig();
        const changes = modified && (!this.#current || !isEqual(config, this.#current));
        if (changes) {
            this.#previous = this.#current;
            this.#current = this.#freeze(config);
            this.sync();
            this.#logger.debug('updated config with changes from remote');
        }
        this.#loadedStatusAtom.set({
            remoteConfigUrl: this.#remoteConfigUrl,
            error: null,
            loaded: true,
        });
        this.#resolveLoaded();
    };
    #fetchConfig = async () => {
        if (!this.#remoteConfigUrl) {
            this.#remoteConfigUrl = await generateRemoteConfigUrl(this.#getBuildMetadata);
        }
        const url = `${this.#remoteConfigUrl}?cacheBump=0`;
        const headers = { Accept: 'application/json' };
        const method = this.#lastModified ? 'head' : 'get';
        this.#logger.debug(`trying to fetch remote config from ${this.#remoteConfigUrl} ...`);
        let response = await synchronizeTime(() => this.#fetch(url, { method, headers }));
        const lastModified = response.headers.get('Last-Modified');
        if (lastModified && lastModified === this.#lastModified) {
            this.#logger.debug('remote config unmodified');
            return { modified: false };
        }
        if (this.#lastModified) {
            response = await this.#fetch(url, { headers });
        }
        const { data } = await response.json();
        if (!data || typeof data !== 'object') {
            throw new TypeError('invalid body data');
        }
        if (lastModified) {
            this.#lastModified = lastModified;
        }
        this.#logger.debug('remote config fetched');
        return { config: data, modified: true };
    };
}
const createRemoteConfig = ({ freeze, logger, fetch, config, getBuildMetadata, remoteConfigStatusAtom, }) => {
    return new RemoteConfig({
        logger,
        fetch,
        freeze,
        config,
        getBuildMetadata,
        remoteConfigStatusAtom,
    });
};
const remoteConfigDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: createRemoteConfig,
    dependencies: [
        'fetch',
        'freeze',
        'logger',
        'remoteConfigStatusAtom',
        'getBuildMetadata',
        'config?',
    ],
    public: true,
};
export default remoteConfigDefinition;
